// Shared Sentry error tracking wrapper
// Used by apps/web, apps/admin and apps/hub — import from @startus/lib

import * as Sentry from '@sentry/react';

const isDev = import.meta.env.DEV;

interface InitSentryOptions {
  // Tags each error with the originating app — visible in Sentry filters
  appName: 'web' | 'admin' | 'hub';
}

// Initialize Sentry — call once before ReactDOM.createRoot in each app
// No-ops silently if DSN is missing (e.g. local dev without config)
export function initSentry({ appName }: InitSentryOptions): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (!dsn) {
    if (isDev) console.warn('[Sentry] Skipped — VITE_SENTRY_DSN not set');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: import.meta.env.VITE_APP_VERSION as string | undefined,
    integrations: [Sentry.browserTracingIntegration()],
    // Sample 10% of transactions in prod — all in dev
    tracesSampleRate: isDev ? 1.0 : 0.1,
    // Drop events in dev — use console instead to avoid noise in Sentry
    beforeSend(event) {
      if (isDev) {
        console.log('[Sentry] Would send event:', event);
        return null;
      }
      return event;
    },
  });

  // Tag every error with which app it came from
  Sentry.setTag('app', appName);

  if (isDev)
    console.log(`[Sentry] Initialized for "${appName}" (dev mode — events will NOT be sent)`);
}

// Set user context after login — links errors to a specific person by id only.
// The email is PII and must not reach Sentry; the id is enough to look the user up.
// `_email` is kept (ignored) only so existing callers keep compiling — deprecated,
// drop it from call sites and then from this signature.
export function setSentryUser(userId: string, _email?: string): void {
  Sentry.setUser({ id: userId });
}

// Clear user context on logout — prevents cross-user error attribution
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

// Tag the current feature area — helps filter errors by section in Sentry
export function setSentryTag(key: string, value: string): void {
  Sentry.setTag(key, value);
}

// Manually capture a caught exception with optional context
// Use in catch blocks where the error is handled but still worth reporting
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (isDev) {
    console.error('[Sentry] captureException:', error, context ?? '');
    return;
  }
  Sentry.withScope((scope) => {
    if (context) scope.setExtras(context);
    Sentry.captureException(error);
  });
}

export { Sentry };
